/*
 * Peter Beales Roses - Rose Finder
 * ---------------------------------------------------------------------------
 * Steps through the question blocks, then matches the answers against the
 * product cards Liquid rendered (hidden) in the results pool.
 *
 * How matching works
 *   - sections/rose-finder.liquid stamps every pool card with that product's
 *     raw catalogue data (tags, product type, and the custom.* metafields).
 *   - computeTraits() below turns that raw data into a set of normalised
 *     traits such as "colour:pink", "pos:pots", "form:climber",
 *     "fragrance:highly". Metafields win; tags are only used where the
 *     metafield is empty, because the product page shows the metafield.
 *   - Each quiz option's "value" in the theme editor is one trait key, so a
 *     customer's answers are a list of traits a rose must carry.
 *   - Roses that meet every answer are listed first. If there are few, roses
 *     that miss exactly one answer are listed as "close matches", with the
 *     missed answer named on the card, so results are never silently wrong.
 *
 * The pure part (window.pbRoseFinderCore / module.exports) has no DOM access
 * and is unit-tested outside the browser.
 */
(function () {
  'use strict';

  /* ======================================================================
     Pure matching core
     ====================================================================== */
  var core = (function () {
    function norm(s) {
      return String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim();
    }

    // "A, B | C" -> ["a", "b", "c"]
    function splitList(s) {
      return norm(s).split(/\s*[|,;]\s*/).filter(Boolean);
    }

    function inList(list, values) {
      for (var i = 0; i < values.length; i++) {
        if (list.indexOf(values[i]) !== -1) return true;
      }
      return false;
    }

    function anyMatch(list, re) {
      for (var i = 0; i < list.length; i++) {
        if (re.test(list[i])) return true;
      }
      return false;
    }

    // tag value for a "key:value" tag, e.g. tagValue(tags, "thorns") -> "moderate"
    function tagValues(tags, key) {
      var out = [];
      var prefix = key + ':';
      for (var i = 0; i < tags.length; i++) {
        if (tags[i].indexOf(prefix) === 0) out.push(tags[i].slice(prefix.length).trim());
      }
      return out;
    }

    var COLOUR_MAP = {
      red: ['red', 'deep red', 'dark red', 'crimson', 'scarlet', 'burgundy', 'orange-red', 'orange red', 'red and yellow', 'yellow and red'],
      pink: ['pink', 'light pink', 'dark pink', 'soft pink', 'pale pink', 'pink and cream striped', 'cream with pink blush'],
      orange: ['orange', 'orange-red', 'orange red', 'apricot', 'peach', 'salmon', 'copper'],
      yellow: ['yellow', 'red and yellow', 'yellow and red', 'gold', 'golden yellow'],
      white: ['white', 'cream', 'white/cream', 'white / cream', 'cream with pink blush', 'pink and cream striped', 'ivory'],
      purple: ['purple', 'violet', 'brownish mauve', 'mauve', 'lilac', 'lavender', 'lavender/mauve'],
      multi: ['multi coloured', 'multi-coloured', 'multicoloured', 'bi-colour', 'bi colour', 'bicolour', 'unique colour', 'unusual colour', 'red and yellow', 'yellow and red', 'pink and cream striped', 'striped']
    };

    function fragranceLevelOf(text) {
      // 0 unknown, 1 little/none/mild, 2 fragrant, 3 highly fragrant
      var t = norm(text);
      if (!t) return 0;
      if (/little|no fragrance|^none|mild|slight/.test(t)) return 1;
      if (/highly|very fragrant|very scented|strong|intense|heavily|richly/.test(t)) return 3;
      if (/fragrant|scent/.test(t)) return 2;
      return 0;
    }

    function fragranceLevel(mf, tags) {
      var fromMeta = fragranceLevelOf(mf.fragrance);
      if (fromMeta) return fromMeta;
      // tags: explicit fragrance:* first, then the plain descriptive tags
      var explicit = tagValues(tags, 'fragrance');
      var best = 0;
      for (var i = 0; i < explicit.length; i++) best = Math.max(best, fragranceLevelOf(explicit[i]));
      if (best) return best;
      if (inList(tags, ['highly fragrant', 'very fragrant', 'highly scented roses'])) return 3;
      if (inList(tags, ['fragrant'])) return 2;
      if (inList(tags, ['little or no fragrance', 'no fragrance', 'mild fragrance', 'mild'])) return 1;
      // strength tags (Strong / Moderate / Light) only when nothing else says
      if (inList(tags, ['strong'])) return 3;
      if (inList(tags, ['moderate'])) return 2;
      if (inList(tags, ['light'])) return 1;
      return 0;
    }

    // First number + unit in "90x60cm (3x2ft)", "Up to about 2.1 m", "450cm x 300cm"
    function heightCm(text) {
      var t = norm(text);
      if (!t) return 0;
      var num = t.match(/(\d+(?:\.\d+)?)/);
      if (!num) return 0;
      var n = parseFloat(num[1]);
      if (/cm/.test(t)) return n;
      if (/\bm\b/.test(t) || /\dm\b/.test(t)) return n * 100;
      if (/ft|'/.test(t)) return n * 30.48;
      return 0;
    }

    function computeTraits(p) {
      var mf = p.mf || {};
      var tags = (p.tags || []).map(norm).filter(Boolean);
      var typeText = norm(p.type) + '|' + norm(mf.family);
      var traits = {};
      function add(k) { traits[k] = true; }

      /* ---- where it will grow ---- */
      var pos = splitList(mf.suitable).concat(splitList(mf.suitableFor));
      var suitableTags = tags.filter(function (t) { return t.indexOf('suitable:') === 0; })
        .map(function (t) { return t.slice(9); });
      pos = pos.concat.apply(pos, suitableTags.map(splitList));

      if (anyMatch(pos, /\bpots?\b/) || inList(tags, ['suitable for pots'])) add('pos:pots');
      if (anyMatch(pos, /border/) || inList(tags, ['good for borders'])) add('pos:borders');
      if (anyMatch(pos, /hedg/) || inList(tags, ['suitable for hedges', 'hedging'])) add('pos:hedge');
      var northWall = anyMatch(pos, /north.*wall/) || inList(tags, ['north facing walls']);
      if (anyMatch(pos, /shade/) || inList(tags, ['good for shade', 'shade tolerant', 'shade']) || northWall) add('pos:shade');
      if (anyMatch(pos, /wall|fence|trellis/) || inList(tags, ['good for walls and fences']) || northWall) add('pos:wall');
      if (anyMatch(pos, /arch|pergola|arbour|obelisk|pillar|doorway/) ||
          inList(tags, ['good for arches and pergolas', 'good for doorways'])) add('pos:arch');
      if (anyMatch(pos, /tree/) || inList(tags, ['useful for growing through trees'])) add('pos:tree');
      if (anyMatch(pos, /poor/) || inList(tags, ['tolerant to poorer soils'])) add('pos:poorsoil');

      /* ---- type of rose ---- */
      var formClimber = inList(tags, ['climbing roses', 'climber', 'climbers', 'climbing', 'patio climber', 'patio climbers & ramblers', 'species climbers & ramblers', 'hybrid – large-flowered climbing rose']) || /climber/.test(typeText);
      var formRambler = inList(tags, ['rambling roses', 'rambler', 'repeat ramblers', 'patio climbers & ramblers', 'species climbers & ramblers']) || /rambler/.test(typeText);
      var formShrub = inList(tags, ['shrub roses', 'shrub', 'shrubs', 'modern shrubs', 'species shrubs', 'the shrub species', 'shrub roses that can be grown as climbers']) || /shrub/.test(typeText);
      var formBush = inList(tags, ['bush', 'hybrid teas', 'floribundas (cluster roses)', 'floribunda']) || /hybrid tea|floribunda/.test(typeText);
      var formPatio = inList(tags, ['patio roses', 'patio', 'miniature', 'perfect roses for small gardens']) || /patio|miniature|polyantha/.test(typeText);
      var formGround = inList(tags, ['procumbent roses', 'procumbent (ground covering roses)']) || /procumbent/.test(typeText);
      var formStandard = anyMatch(tags, /standard/);
      // A standard (tree rose) is its own product that inherits its parent
      // rose's tags, so it counts as a standard only - never as a bush/climber.
      if (formStandard) {
        add('form:standard');
      } else {
        if (formClimber) add('form:climber');
        if (formRambler) add('form:rambler');
        if (formShrub) add('form:shrub');
        // a climbing sport of a floribunda/hybrid tea is not also a bush rose
        if (formBush && !formClimber && !formRambler) add('form:bush');
        if (formPatio) add('form:patio');
        if (formGround) add('form:ground');
      }

      /* ---- colour (union of metafield + tags: bicolours carry several) ---- */
      var colourWords = splitList(mf.colour).concat(tags).concat(tagValues(tags, 'colour'));
      Object.keys(COLOUR_MAP).forEach(function (c) {
        if (inList(colourWords, COLOUR_MAP[c])) add('colour:' + c);
      });

      /* ---- fragrance (cumulative: highly fragrant is also fragrant) ---- */
      var fl = fragranceLevel(mf, tags);
      if (fl >= 2) add('fragrance:fragrant');
      if (fl >= 3) add('fragrance:highly');

      /* ---- size ---- */
      var h = heightCm(mf.height);
      if (!h) {
        var ht = tagValues(tags, 'height');
        for (var i = 0; i < ht.length && !h; i++) h = heightCm(ht[i]);
      }
      if (h) {
        if (h <= 100) add('size:compact');
        else if (h <= 180) add('size:medium');
        else if (h <= 300) add('size:tall');
        else add('size:giant');
      }

      /* ---- extras ---- */
      var cutMeta = norm(mf.cutting);
      if (cutMeta === 'yes' || (!cutMeta && (inList(tags, ['cutting:yes', 'good for cut flowers', 'good for cuttings']))) ||
          anyMatch(pos, /cut flower|cutting garden/)) add('extra:cutting');

      var repMeta = norm(mf.repeat);
      if (repMeta ? repMeta === 'yes' : inList(tags, ['repeat flowering', 'repeat-flowering:yes', 'repeat ramblers'])) add('extra:repeat');

      var wlMeta = norm(mf.wildlife);
      if (wlMeta ? wlMeta === 'yes' : inList(tags, ['wildlife friendly', 'wildlife:yes', 'wildlife:wildlife friendly'])) add('extra:wildlife');

      var thornText = norm(mf.thorns) || (tagValues(tags, 'thorns')[0] || '');
      if (/few|little or no|no thorn|^low$|thornless/.test(thornText)) add('extra:fewthorns');

      var disMeta = norm(mf.disease) || (tagValues(tags, 'disease-tolerance')[0] || '');
      if (disMeta === 'very' || disMeta === 'yes' || disMeta === 'high') add('extra:disease');

      return traits;
    }

    // answers: { questionIndex: traitKey }; returns indexes of unmet answers
    function unmetAnswers(traits, answers) {
      var unmet = [];
      Object.keys(answers).forEach(function (q) {
        if (answers[q] && !traits[answers[q]]) unmet.push(q);
      });
      return unmet;
    }

    function readCardData(get) {
      var tagsAttr = get('data-rf-tags') || '';
      return {
        tags: tagsAttr ? tagsAttr.split('|') : [],
        type: get('data-rf-type') || '',
        mf: {
          suitable: get('data-rf-suitable'),
          suitableFor: get('data-rf-suitable-for'),
          colour: get('data-rf-colour'),
          fragrance: get('data-rf-fragrance'),
          disease: get('data-rf-disease'),
          cutting: get('data-rf-cutting'),
          thorns: get('data-rf-thorns'),
          wildlife: get('data-rf-wildlife'),
          repeat: get('data-rf-repeat'),
          height: get('data-rf-height'),
          family: get('data-rf-family')
        }
      };
    }

    return {
      computeTraits: computeTraits,
      unmetAnswers: unmetAnswers,
      readCardData: readCardData,
      heightCm: heightCm,
      fragranceLevel: fragranceLevel,
      norm: norm
    };
  })();

  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof document === 'undefined') return;
  window.pbRoseFinderCore = core;

  /* ======================================================================
     DOM / quiz behaviour
     ====================================================================== */
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var scrollBehavior = reduceMotion ? 'auto' : 'smooth';
  var PAGE_SIZE = 24;       // roses revealed per "Show more"
  var NEAR_BELOW = 12;      // offer close matches when fewer exact matches than this

  // Exposed so theme-editor.js can re-bind after the Theme Editor swaps this
  // section's HTML in for a live preview (shopify:section:load) - this
  // <script src> tag itself never re-executes on that swap, so without this
  // hook every option button would be a dead click after the first edit.
  // Already-bound roots are skipped via data-rf-bound, so calling this again
  // is always safe.
  function initRoseFinder() {
    document.querySelectorAll('[data-rose-finder]').forEach(function (root) {
      if (root.hasAttribute('data-rf-bound')) return;
      root.setAttribute('data-rf-bound', '');

      var steps = [].slice.call(root.querySelectorAll('[data-rf-step]'));
      var questionSteps = steps.filter(function (s) { return s.hasAttribute('data-rf-index'); });
      if (!questionSteps.length) return;

      var result = root.querySelector('[data-rf-result]');
      var resultLead = root.querySelector('[data-rf-result-lead]');
      var statusEl = root.querySelector('[data-rf-status]');
      var chipsEl = root.querySelector('[data-rf-chips]');
      var grid = root.querySelector('[data-rf-results-grid]');
      var nearHeading = root.querySelector('[data-rf-near-heading]');
      var moreBtn = root.querySelector('[data-rf-more]');
      var noMatches = root.querySelector('[data-rf-no-matches]');
      var searchLink = root.querySelector('[data-rf-site-search]');
      var nameForm = root.querySelector('[data-rf-name-form]');
      var nameInput = root.querySelector('[data-rf-name]');
      var progressBar = root.querySelector('[data-rf-progress-bar]');
      var defaultLead = resultLead ? resultLead.textContent : '';

      var cards = [];
      var seen = {};
      var answers = {};      // question index -> trait key
      var labels = {};       // question index -> option label chosen
      var nameQuery = '';
      var current = 0;
      var shown = PAGE_SIZE;
      var pending = 0;       // extra pool pages still being fetched
      var failed = false;
      var finished = false;  // quiz completed (or name search used)

      function registerCard(el) {
        var handle = el.getAttribute('data-rf-handle') || '';
        if (handle && seen[handle]) return false;
        if (handle) seen[handle] = true;
        var rec = {
          el: el,
          title: core.norm(el.getAttribute('data-rf-title')),
          traits: core.computeTraits(core.readCardData(function (a) { return el.getAttribute(a); })),
          note: null
        };
        var note = document.createElement('p');
        note.className = 'rose-finder__unmet';
        note.hidden = true;
        el.appendChild(note);
        rec.note = note;
        cards.push(rec);
        return true;
      }

      [].slice.call(root.querySelectorAll('[data-rf-card]')).forEach(registerCard);

      /* ---- fetch the remaining pool pages (Liquid paginates by 250) ---- */
      function loadMorePages() {
        if (!grid) return;
        var pages = parseInt(grid.getAttribute('data-rf-pages'), 10) || 1;
        var sectionId = grid.getAttribute('data-rf-section');
        if (pages < 2 || !sectionId) return;
        for (var p = 2; p <= pages; p++) {
          (function (page) {
            pending++;
            var url = window.location.pathname + '?page=' + page + '&section_id=' + encodeURIComponent(sectionId);
            fetch(url, { credentials: 'same-origin' })
              .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
              .then(function (html) {
                var doc = new DOMParser().parseFromString(html, 'text/html');
                var incoming = [].slice.call(doc.querySelectorAll('[data-rf-card]'));
                if (!incoming.length) throw new Error('no cards');
                var added = 0;
                incoming.forEach(function (el) {
                  var node = document.importNode(el, true);
                  node.hidden = true;
                  if (registerCard(node)) { grid.appendChild(node); added++; }
                });
                // a page that adds nothing means the server ignored ?page=
                if (!added) throw new Error('duplicate page');
              })
              .catch(function () { failed = true; })
              .then(function () {
                pending--;
                if (finished) render(false);
              });
          })(p);
        }
      }

      /* ---- quiz stepping ---- */
      function show(index, quiet) {
        finished = false;
        questionSteps.forEach(function (s, i) { s.hidden = i !== index; });
        if (result) result.hidden = index < questionSteps.length;
        current = index;
        if (progressBar) {
          var pct = Math.round((index / questionSteps.length) * 100);
          progressBar.style.width = Math.min(100, pct) + '%';
        }
        if (quiet) return;
        var visible = index < questionSteps.length ? questionSteps[index] : result;
        if (visible) {
          var focusable = visible.querySelector('button, a');
          if (focusable) focusable.focus({ preventScroll: true });
          root.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
        }
      }

      function answerKeys() {
        return Object.keys(answers).filter(function (k) { return answers[k]; });
      }

      function classify() {
        var q = nameQuery;
        var exact = [], near1 = [], near2 = [];
        if (q) {
          var words = q.split(' ').filter(Boolean);
          cards.forEach(function (c) {
            var ok = words.every(function (w) { return c.title.indexOf(w) !== -1; });
            if (ok) exact.push({ c: c, unmet: [] });
          });
          return { exact: exact, near: [] };
        }
        cards.forEach(function (c) {
          var unmet = core.unmetAnswers(c.traits, answers);
          if (!unmet.length) exact.push({ c: c, unmet: unmet });
          else if (unmet.length === 1) near1.push({ c: c, unmet: unmet });
          else if (unmet.length === 2) near2.push({ c: c, unmet: unmet });
        });
        var near = [];
        if (exact.length < NEAR_BELOW) near = near1;
        if (!exact.length && !near.length) near = near2;
        return { exact: exact, near: near };
      }

      function renderChips() {
        if (!chipsEl) return;
        chipsEl.textContent = '';
        function chip(text, onRemove) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'rose-finder__chip';
          b.setAttribute('aria-label', 'Remove ' + text);
          b.textContent = text + ' ×';
          b.addEventListener('click', onRemove);
          chipsEl.appendChild(b);
        }
        if (nameQuery) {
          chip('Name: ' + nameQuery, function () {
            nameQuery = '';
            if (nameInput) nameInput.value = '';
            if (!answerKeys().length) { restart(); return; }
            render(true);
          });
          return;
        }
        answerKeys().forEach(function (k) {
          chip(labels[k] || answers[k], function () {
            delete answers[k];
            delete labels[k];
            var step = questionSteps[parseInt(k, 10)];
            if (step) step.querySelectorAll('[data-rf-option]').forEach(function (b) { b.classList.remove('is-selected'); });
            render(true);
          });
        });
      }

      function render(scroll) {
        if (!grid) return;
        finished = true;
        questionSteps.forEach(function (s) { s.hidden = true; });
        if (result) result.hidden = false;
        if (progressBar) progressBar.style.width = '100%';

        var res = classify();
        var exact = res.exact, near = res.near;
        var limit = shown;
        var order = 1;

        cards.forEach(function (c) {
          c.el.hidden = true;
          c.note.hidden = true;
        });
        exact.forEach(function (m, i) {
          if (i >= limit) return;
          m.c.el.hidden = false;
          m.c.el.style.order = order++;
        });
        var nearShown = 0;
        if (nearHeading) {
          nearHeading.style.order = order++;
        }
        near.forEach(function (m) {
          if (nearShown >= limit) return;
          m.c.el.hidden = false;
          m.c.el.style.order = order++;
          var missed = m.unmet.map(function (k) { return labels[k] || answers[k]; }).join(', ');
          m.c.note.textContent = 'Close match – not ' + missed;
          m.c.note.hidden = false;
          nearShown++;
        });
        if (nearHeading) nearHeading.hidden = !near.length;

        var totalToShow = exact.length + near.length;
        if (moreBtn) moreBtn.hidden = !(exact.length > limit || near.length > limit);

        var loading = pending > 0;
        var lead;
        if (nameQuery) {
          lead = exact.length
            ? (exact.length === 1 ? '1 rose' : exact.length + ' roses') + ' found for “' + nameQuery + '”.'
            : 'No roses in this finder match “' + nameQuery + '”.';
        } else if (!answerKeys().length) {
          lead = 'Here are our most popular roses.';
        } else if (exact.length) {
          lead = (exact.length === 1 ? '1 rose matches' : exact.length + ' roses match') + ' everything you chose.';
        } else if (near.length) {
          lead = 'No rose matches everything you chose. Here are the closest, or remove a choice above to widen your search.';
        } else {
          lead = 'No rose matches those choices. Remove one of your choices above to widen your search.';
        }
        if (resultLead) resultLead.textContent = lead || defaultLead;

        if (statusEl) {
          if (loading) statusEl.textContent = 'Still checking the rest of our range…';
          else if (failed) statusEl.textContent = 'We could not load our full range just now, so some roses may be missing. Try refreshing, or browse the full range below.';
          else statusEl.textContent = '';
        }
        if (noMatches) noMatches.hidden = !(totalToShow === 0 && !loading);
        if (searchLink) {
          searchLink.hidden = !nameQuery && totalToShow > 0;
          if (nameQuery) searchLink.href = '/search?type=product&q=' + encodeURIComponent(nameQuery);
        }

        renderChips();
        if (scroll) root.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
      }

      function finish() {
        shown = PAGE_SIZE;
        render(true);
        var firstFocusable = result && result.querySelector('a, button');
        if (firstFocusable) firstFocusable.focus({ preventScroll: true });
      }

      function restart() {
        answers = {};
        labels = {};
        nameQuery = '';
        shown = PAGE_SIZE;
        if (nameInput) nameInput.value = '';
        root.querySelectorAll('[data-rf-option]').forEach(function (b) { b.classList.remove('is-selected'); });
        cards.forEach(function (c) { c.el.hidden = true; c.note.hidden = true; });
        show(0);
      }

      root.addEventListener('click', function (e) {
        var opt = e.target.closest('[data-rf-option]');
        var back = e.target.closest('[data-rf-back]');
        var now = e.target.closest('[data-rf-now]');
        var edit = e.target.closest('[data-rf-edit]');
        var more = e.target.closest('[data-rf-more]');
        var restartBtn = e.target.closest('[data-rf-restart]');

        if (opt) {
          var step = opt.closest('[data-rf-step]');
          var idx = step.getAttribute('data-rf-index');
          var value = (opt.getAttribute('data-rf-value') || '').toLowerCase().trim();
          if (value) {
            answers[idx] = value;
            labels[idx] = (opt.textContent || '').replace(/\s+/g, ' ').trim();
          } else {
            delete answers[idx];
            delete labels[idx];
          }
          nameQuery = '';
          step.querySelectorAll('[data-rf-option]').forEach(function (b) { b.classList.remove('is-selected'); });
          opt.classList.add('is-selected');
          if (current + 1 < questionSteps.length) show(current + 1);
          else finish();
        }

        if (back && current > 0) show(current - 1);
        if (now) finish();
        if (edit) show(0);
        if (more) { shown += PAGE_SIZE; render(false); }
        if (restartBtn) restart();
      });

      if (nameForm) {
        nameForm.addEventListener('submit', function (e) {
          e.preventDefault();
          var q = core.norm(nameInput ? nameInput.value : '');
          if (!q) return;
          nameQuery = q;
          shown = PAGE_SIZE;
          render(true);
        });
      }

      show(0, true);
      // Pool pages beyond the first load in the background so the whole range
      // is searchable by the time the quiz is finished.
      if ('requestIdleCallback' in window) window.requestIdleCallback(loadMorePages, { timeout: 2500 });
      else window.setTimeout(loadMorePages, 800);
    });
  }

  initRoseFinder();
  window.pbInitRoseFinder = initRoseFinder;
})();
